import { useState } from "react";
import { Loader2, Download, Share2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { downloadCard, shareCard } from "@/lib/api";
import SocialShare from "@/components/SocialShare";

const STYLES = [
  { id: "parchment", label: "Parchment" },
  { id: "dark", label: "Dark" },
  { id: "minimal", label: "Minimal" },
];

export default function CardDialog({ open, onOpenChange, endpoint, filenamePrefix, shareText, shareUrl }) {
  const [style, setStyle] = useState("parchment");
  const [imgLoaded, setImgLoaded] = useState(false);
  const [busy, setBusy] = useState(null); // 'download' | 'share'

  const url = `${endpoint}?style=${style}`;

  const pick = (s) => {
    if (s === style) return;
    setImgLoaded(false);
    setStyle(s);
  };

  const onDownload = async () => {
    setBusy("download");
    try {
      await downloadCard(url, `${filenamePrefix}-${style}.png`);
      toast.success("Your verse card has been downloaded.");
    } catch {
      toast.error("The card could not be created. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const onShare = async () => {
    setBusy("share");
    try {
      const result = await shareCard(url, `${filenamePrefix}-${style}.png`, shareText);
      if (result === "downloaded") toast.success("Sharing isn't available here, so the card was downloaded instead.");
      else if (result === "shared") toast.success("Shared. Choose an app to send it.");
    } catch {
      toast.error("The card could not be shared. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="card-dialog" className="max-w-md bg-background">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-tight">Save this as a card</DialogTitle>
          <DialogDescription className="font-sans">Pick a look, then download or share it.</DialogDescription>
        </DialogHeader>

        <div className="mt-1 flex justify-center gap-2">
          {STYLES.map((s) => (
            <button
              key={s.id}
              data-testid={`card-style-${s.id}`}
              onClick={() => pick(s.id)}
              className={`rounded-full border px-4 py-1.5 font-sans text-sm transition-colors ${
                style === s.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="relative mx-auto mt-4 flex aspect-[4/5] w-64 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
          {!imgLoaded && <Loader2 className="absolute h-6 w-6 animate-spin text-primary" />}
          <img
            data-testid="card-preview"
            src={url}
            alt={`${style} card preview`}
            onLoad={() => setImgLoaded(true)}
            className={`h-full w-full object-cover transition-opacity duration-300 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          />
        </div>

        <div className="mt-4 flex gap-3">
          <Button data-testid="card-share" onClick={onShare} disabled={!!busy} className="flex-1 rounded-full font-sans">
            {busy === "share" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share2 className="mr-2 h-4 w-4" />}
            Share
          </Button>
          <Button data-testid="card-download" onClick={onDownload} disabled={!!busy} variant="outline" className="flex-1 rounded-full font-sans">
            {busy === "download" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Download
          </Button>
        </div>

        {shareUrl && (
          <div className="mt-5 border-t border-border pt-4">
            <p className="mb-3 text-center font-sans text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Or share the link
            </p>
            <SocialShare url={shareUrl} text={shareText} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
